import { FC, useCallback } from 'react';
import { css } from '@emotion/react';
import { Col, Row } from 'reactstrap';

import { useForm, useFormState } from '@/utils/hooks';
import { CompanySettings } from '@/types/company-settings';
import config, {
  evaluations_range_from,
  evaluations_range_to,
  evaluations_step,
  EvaluationsSettingsType
} from 'views/companies/settings/evaluations-settings/config';
import { FieldsStateType } from '@/common/types/form';

import Category from 'views/companies/settings/Category';
import SubCategory from 'views/companies/settings/SubCategory';
import VisibleSetting from 'views/companies/settings/VisibleSetting';
import InputLabel from 'views/companies/settings/components/InputLabel';
import NumberInput from '@/library/components/form/inputs/NumberInput';
import { CATEGORY_IDS } from '../constants';
import { useCategoryDraft } from '../useCategoryDraft';
import { formatDisplayValue } from '../change-tracking';
import { useCompanySettingsDraft } from '../draft-context';

type EvaluationsSettingsProps = {
  companySettings: CompanySettings | undefined;
};

const CATEGORY_ID = CATEGORY_IDS.evaluations;
const CATEGORY_TITLE = 'Evaluations';

const LABELS = {
  [evaluations_range_from]: 'Evaluations grade range from',
  [evaluations_range_to]: 'Evaluations grade range to',
  [evaluations_step]: 'Evaluations grade step'
};

const EvaluationsSettings: FC<EvaluationsSettingsProps> = ({ companySettings }) => {
  const { formState, loadValues, collectValues } = useForm(config);
  const { changeField, ...rest } = useFormState(formState);
  const fields: FieldsStateType<EvaluationsSettingsType> = rest.fields;
  const { isSettingVisible } = useCompanySettingsDraft();

  const initializeForm = useCallback(() => {
    if (!companySettings) return;

    loadValues({
      [evaluations_range_from]: companySettings.evaluations_range_from,
      [evaluations_range_to]: companySettings.evaluations_range_to,
      [evaluations_step]: companySettings.evaluations_step
    });
  }, [companySettings, loadValues]);

  useCategoryDraft({
    categoryId: CATEGORY_ID,
    categoryTitle: CATEGORY_TITLE,
    companySettings,
    initializeForm,
    collectValues: (validate = true) => collectValues(validate) as EvaluationsSettingsType | null,
    toPayload: values => [
      { key: evaluations_range_from, value: values[evaluations_range_from] },
      { key: evaluations_range_to, value: values[evaluations_range_to] },
      { key: evaluations_step, value: values[evaluations_step] }
    ],
    labels: LABELS,
    formatValue: (_key, value) => formatDisplayValue(value),
    watch: fields
  });

  return (
    <Category id={CATEGORY_ID} title={CATEGORY_TITLE}>
      <SubCategory
        title="Range"
        hidden={
          !isSettingVisible(CATEGORY_ID, evaluations_range_from) &&
          !isSettingVisible(CATEGORY_ID, evaluations_range_to) &&
          !isSettingVisible(CATEGORY_ID, evaluations_step)
        }
        className="cpb-2 cmb-2"
      >
        <Row
          className="d-flex align-items-center gx-1"
          css={css`
            .form-field {
              width: 8.5rem;
            }
          `}
        >
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={evaluations_range_from}>
              <NumberInput
                className="mb-0"
                innerClassName="h-24"
                label={
                  <InputLabel
                    title="EVALUATIONS GRADE RANGE FROM"
                    apiKey={evaluations_range_from}
                  />
                }
                decimalScale={0}
                placeholder="Add no."
                onChange={changeField(evaluations_range_from)}
                {...fields[evaluations_range_from]}
              />
            </VisibleSetting>
          </Col>
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={evaluations_range_to}>
              <NumberInput
                className="mb-0"
                innerClassName="h-24"
                label={
                  <InputLabel title="EVALUATIONS GRADE RANGE TO" apiKey={evaluations_range_to} />
                }
                placeholder="Add no."
                decimalScale={0}
                onChange={changeField(evaluations_range_to)}
                {...fields[evaluations_range_to]}
              />
            </VisibleSetting>
          </Col>
          <Col xs={3}>
            <VisibleSetting categoryId={CATEGORY_ID} settingKey={evaluations_step}>
              <NumberInput
                className="mb-0"
                innerClassName="h-24"
                label={<InputLabel title="EVALUATIONS GRADE STEP" apiKey={evaluations_step} />}
                placeholder="Add no."
                decimalScale={1}
                onChange={changeField(evaluations_step)}
                {...fields[evaluations_step]}
              />
            </VisibleSetting>
          </Col>
        </Row>
      </SubCategory>
    </Category>
  );
};

export default EvaluationsSettings;
